/**
 * NUITS ET REJEU — le modèle (21/10/2026). Fonctions pures, sans React.
 *
 * Une nuit de Sonde est la nuit COMMERCIALE : de 08:00 à 08:00, heure de
 * Paris (coupure réglée par `schedule.commercial_night_cutoff`). Sa date est
 * celle de l'affiche : 01:30 le vendredi appartient à la nuit du jeudi.
 *
 * Toutes les heures sont calculées en millisecondes UTC et affichées en heure
 * de Paris par `Intl` : jamais d'addition de « 9 heures » à une heure murale.
 * La nuit du 24 au 25/10/2026 dure 25 heures (retour à l'heure d'hiver à
 * 03:00) : de 02:00 à 03:00, chaque minute existe deux fois, et l'heure
 * affichée dit laquelle (« 02:30 (été) », « 02:30 (hiver) »).
 *
 * Gardes de forme : une réponse qui n'a pas la forme du contrat v1 n'est
 * jamais peinte comme une donnée (voir `lireSupervision`).
 */
import type { Ton } from '../commun.js'

/* ═══════════════ Formes de Sonde (contrat v1, nights.ts et annotations.ts) ═══════════════ */

export interface PieceSalle { zone: string; on_at: string | null; off_at: string | null; cut_at: string | null; at_start: string }

export interface ComptesNuit {
  incidents: number
  incidents_critical: number
  alerts: number
  alerts_shadow: number
  offline_transitions: number
  devices_offline: number
  anomalies: number
  events: Record<string, number>
}

/** Trou de supervision : une source indisponible ≥ 1 min. Ses appareils n'ont pas été vus, ni en ligne ni en panne. */
export interface TrouSupervision { source: string; from: string; to: string | null; seconds: number }

export interface Nuit {
  night_date: string
  starts_at: string
  ends_at: string
  status: 'open' | 'closed'
  kind: 'soiree' | 'salle_allumee' | 'fermee' | null
  operating: boolean | null
  origin: string | null
  label: string | null
  opening_at: string | null
  closing_at: string | null
  zones: string[]
  rooms: PieceSalle[]
  counts: ComptesNuit | null
  preflight: { lead_min: number; status: string; run_at: string }[]
  sources_unavailable: TrouSupervision[]
  pinned: { at: string; by: string; reason: string | null; expires_at: string | null } | null
  report: { status: string; subject: string; generated_at: string; sent_at: string | null } | null
  computed_at: string | null
}

export interface ListeNuits { from: string; to: string; nights: Nuit[]; pinned_count: number; pinned_max: number }

export type StatutVerif = 'ok' | 'warn' | 'fail' | 'unknown' | 'waiting' | 'skipped'

export interface PreVol {
  night_date: string
  lead_min: number
  opening_at: string
  computed_at: string
  status: 'green' | 'amber' | 'red' | 'unknown'
  summary_fr: string
  checks: {
    code: string
    label: string
    status: StatutVerif
    message_fr: string
    items: { uid: string | null; name: string; status: StatutVerif; detail_fr: string; value: number | null }[]
  }[]
}

export type StatutRapport = 'ok' | 'attention' | 'probleme' | 'inconnu'

export interface RapportMatin {
  night_date: string
  generated_at: string
  status: StatutRapport
  subject: string
  sections: { code: string; title: string; status: StatutRapport; lines: string[] }[]
  witness_fr: string
  shadow_mode: boolean
  shadow_release?: string
  shadow_published_sources?: number | null
}

export interface Constat {
  id: string
  kind: string
  uid: string | null
  severity: 'info' | 'warning' | 'error' | 'critical'
  title: string
  first_seen_at: string
  last_seen_at: string
  resolved_at: string | null
  occurrences: number
}

export interface IncidentNuit {
  id: string
  title: string
  severity: string
  status: string
  opened_at: string
  resolved_at: string | null
  root_uid: string | null
  affected_count: number
}

export interface DetailNuit {
  night: Nuit
  data: { detail: 'raw' | 'partial' | 'none'; archived_parts: { part: string; rows: number; size_bytes: number; created_at: string }[] }
  preflight: PreVol[]
  anomalies: Constat[]
  anomaly_state: { state: string; message_fr: string; learned_nights: number; needed_nights: number } | null
  incidents: IncidentNuit[]
  report: RapportMatin | null
}

export type EtatAffiche = 'ONLINE' | 'DEGRADED' | 'OFFLINE' | 'OFF_EXPECTED' | 'MAINTENANCE' | 'UNKNOWN' | 'STALE' | 'UNAVAILABLE'
type Scalaire = number | string | null

export interface AppareilRejeu {
  uid: string
  name: string
  kind: string
  family: string
  zone: string | null
  criticality: string
  status: string
  display_state: EtatAffiche
  observation: string | null
  cause: string | null
  cause_ref: string | null
  since: string | null
  flapping: boolean
  transitions_10min: number
  metrics: Record<string, Scalaire>
  uplink: { switch_uid: string; switch_name: string; port: number | null } | null
}

export interface Rejeu {
  night_date: string
  at: string
  zone: string | null
  bounds: { starts_at: string; ends_at: string }
  resolution: 'raw' | '5m' | '1h' | 'none'
  data_note_fr: string | null
  collector_running: boolean | null
  display_counts: Record<string, number>
  zones: { code: string; power: 'on' | 'off' | 'unknown'; since: string | null; cut: boolean }[]
  counts: Record<string, number>
  devices: AppareilRejeu[]
  events: { at: string; kind: string; severity: string; uid: string | null; entity_key: string | null; message_fr: string | null }[]
  incidents: { id: string; title: string; severity: string; opened_at: string; resolved_at: string | null; open_at_t: boolean; root_uid: string | null; affected_count: number }[]
  context: { entity_key: string; metric: string; value: number | null; at: string }[]
  timeline: { step_s: number; points: { at: string; down: number; up: number; events_warning: number }[] }
}

export type GenreAnnotation = 'note' | 'balance' | 'intervention' | 'incident' | 'information'

export interface Annotation {
  id: string
  at: string
  night_date: string
  kind: GenreAnnotation
  kind_label: string
  body: string
  zone_code: string | null
  device: { uid: string; display_name: string } | null
  incident_id: string | null
  author: { id: string; name: string }
  created_at: string
  deleted_at: string | null
  deleted_by: string | null
}

export interface ListeAnnotations { items: Annotation[]; next_cursor: string | null }

/* ═══════════════ Gardes de forme ═══════════════ */

const obj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const txt = (x: unknown): x is string => typeof x === 'string'
const txtNul = (x: unknown) => x === null || typeof x === 'string'
const tab = (x: unknown): x is unknown[] => Array.isArray(x)
const nb = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x)
const DATE_NUIT = /^\d{4}-\d{2}-\d{2}$/

export function estNuit(x: unknown): x is Nuit {
  return obj(x) && txt(x['night_date']) && DATE_NUIT.test(x['night_date']) && txt(x['starts_at']) && txt(x['ends_at'])
    && (x['status'] === 'open' || x['status'] === 'closed') && tab(x['rooms']) && tab(x['sources_unavailable'])
    && (x['counts'] === null || obj(x['counts'])) && (x['pinned'] === null || obj(x['pinned'])) && (x['report'] === null || obj(x['report']))
    && (x['sources_unavailable'] as unknown[]).every((t) => obj(t) && txt(t['source']) && txt(t['from']) && txtNul(t['to']))
}

export function estListeNuits(x: unknown): x is ListeNuits {
  return obj(x) && tab(x['nights']) && (x['nights'] as unknown[]).every(estNuit) && nb(x['pinned_count']) && nb(x['pinned_max'])
}

export function estDetailNuit(x: unknown): x is DetailNuit {
  return obj(x) && estNuit(x['night']) && obj(x['data']) && tab(x['preflight']) && tab(x['anomalies']) && tab(x['incidents'])
    && (x['report'] === null || (obj(x['report']) && tab(x['report']['sections'])))
}

export function estRejeu(x: unknown): x is Rejeu {
  if (!obj(x) || !txt(x['at']) || !obj(x['bounds']) || !tab(x['devices']) || !tab(x['zones']) || !tab(x['events'])) return false
  if (!tab(x['incidents']) || !obj(x['display_counts']) || !obj(x['timeline']) || !tab(x['timeline']['points'])) return false
  return (x['devices'] as unknown[]).every((d) => obj(d) && txt(d['uid']) && txt(d['display_state']))
}

export function estRapport(x: unknown): x is RapportMatin {
  return obj(x) && txt(x['subject']) && txt(x['generated_at']) && tab(x['sections']) && txt(x['status'])
}

export function estListeAnnotations(x: unknown): x is ListeAnnotations {
  return obj(x) && tab(x['items']) && (x['items'] as unknown[]).every((a) => obj(a) && txt(a['id']) && txt(a['at']) && txt(a['body']))
}

/** Libellés des salles et des sources, lus dans `zones` et `status` (facultatifs : le code reste lisible). */
export function estZones(x: unknown): x is { zones: { code: string; label: string; kind?: string }[] } {
  return obj(x) && tab(x['zones']) && (x['zones'] as unknown[]).every((z) => obj(z) && txt(z['code']) && txt(z['label']))
}
export function estStatut(x: unknown): x is { sources: { code: string; label: string }[]; collector?: { ok?: boolean | null; stale?: boolean | null; detail?: string | null } } {
  return obj(x) && tab(x['sources']) && (x['sources'] as unknown[]).every((s) => obj(s) && txt(s['code']))
}

/* ═══════════════ Heures de Paris ═══════════════ */

const TZ = 'Europe/Paris'
const FMT_PARTIES = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
})

export interface PartiesParis { y: number; mo: number; d: number; h: number; mi: number; s: number }

export function partiesParis(ms: number): PartiesParis {
  const p: Record<string, number> = {}
  for (const x of FMT_PARTIES.formatToParts(new Date(ms))) if (x.type !== 'literal') p[x.type] = Number(x.value)
  return { y: p['year']!, mo: p['month']!, d: p['day']!, h: (p['hour'] ?? 0) % 24, mi: p['minute']!, s: p['second']! }
}

/** Décalage de Paris sur UTC à cet instant, en minutes (+120 l'été, +60 l'hiver). */
export function decalageParis(ms: number): number {
  const p = partiesParis(ms)
  const mural = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s)
  return Math.round((mural - Math.floor(ms / 1000) * 1000) / 60_000)
}

/** Instant UTC (ms) d'une heure murale de Paris. Pour une heure qui existe deux fois, la seconde (heure d'hiver). */
export function parisVersUtc(ymd: string, h: number, mi = 0): number {
  const [y, mo, d] = ymd.split('-').map(Number) as [number, number, number]
  const naif = Date.UTC(y, mo - 1, d, h, mi)
  let t = naif - decalageParis(naif) * 60_000
  t = naif - decalageParis(t) * 60_000
  return t
}

export function jourSuivant(ymd: string, n = 1): string {
  const [y, mo, d] = ymd.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, mo - 1, d + n, 12)).toISOString().slice(0, 10)
}

const deux = (n: number) => String(n).padStart(2, '0')

/** Cette heure murale existe-t-elle deux fois (nuit du retour à l'heure d'hiver) ? */
export function heureDouble(ms: number): boolean {
  const p = partiesParis(ms)
  for (const dt of [-3_600_000, 3_600_000]) {
    const q = partiesParis(ms + dt)
    if (q.h === p.h && q.mi === p.mi && q.d === p.d) return true
  }
  return false
}

/** « 01:10 » en heure de Paris ; « 02:30 (été) » / « 02:30 (hiver) » quand l'heure existe deux fois. */
export function heureParis(ms: number | string | null | undefined, secondes = false): string {
  if (ms === null || ms === undefined) return '—'
  const t = typeof ms === 'string' ? Date.parse(ms) : ms
  if (!Number.isFinite(t)) return '—'
  const p = partiesParis(t)
  const base = `${deux(p.h)}:${deux(p.mi)}${secondes ? `:${deux(p.s)}` : ''}`
  if (!heureDouble(t)) return base
  return `${base} (${decalageParis(t) >= 120 ? 'été' : 'hiver'})`
}

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

/** « jeudi 22 octobre » (date d'affiche, sans fuseau : c'est une date, pas un instant). */
export function dateNuit(ymd: string, annee = false): string {
  const [y, mo, d] = ymd.split('-').map(Number) as [number, number, number]
  const j = new Date(Date.UTC(y, mo - 1, d, 12)).getUTCDay()
  return `${JOURS[j]} ${d === 1 ? '1er' : d} ${MOIS[mo - 1]}${annee ? ` ${y}` : ''}`
}

/** « jeu. 22 » pour la mosaïque. */
export function dateCourte(ymd: string): { jour: string; num: string } {
  const [y, mo, d] = ymd.split('-').map(Number) as [number, number, number]
  const j = new Date(Date.UTC(y, mo - 1, d, 12)).getUTCDay()
  return { jour: (JOURS[j] ?? '').slice(0, 3), num: String(d) }
}

/** « 1 h 25 », « 14 min », « 40 s » ; « — » si inconnu. */
export function duree(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s)) return '—'
  if (s < 60) return `${Math.round(s)} s`
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r ? `${h} h ${deux(r)}` : `${h} h`
}

/* ═══════════════ Fenêtre du rejeu ═══════════════ */

export type ModeFenetre = 'soiree' | 'journee'

export interface Fenetre { debut: number; fin: number; mode: ModeFenetre; /** la fin est « maintenant » (nuit en cours) */ enCours: boolean }

/**
 * Fenêtre du curseur. « soiree » : de 22:00 à 07:00 (heure de Paris), bornée
 * par la nuit commerciale et par maintenant (Sonde refuse un instant à venir).
 * Une soirée qui n'a pas encore atteint 22:15 se rabat sur la journée
 * entière (08:00 → maintenant). null : rien à rejouer (nuit à venir).
 */
export function fenetreRejeu(n: Pick<Nuit, 'night_date' | 'starts_at' | 'ends_at'>, mode: ModeFenetre, maintenant: number): Fenetre | null {
  const debutNuit = Date.parse(n.starts_at)
  const finNuit = Date.parse(n.ends_at) - 60_000
  // 30 s de marge : une horloge du navigateur un peu en avance ne demande jamais à Sonde un instant à venir.
  const plafond = Math.min(finNuit, Math.floor((maintenant - 30_000) / 60_000) * 60_000)
  if (!Number.isFinite(debutNuit) || !Number.isFinite(finNuit) || plafond <= debutNuit) return null
  if (mode === 'soiree') {
    const d = Math.max(debutNuit, parisVersUtc(n.night_date, 22))
    const f = Math.min(plafond, parisVersUtc(jourSuivant(n.night_date), 7))
    // Moins d'un quart d'heure de soirée écoulé : la journée entière reste plus utile.
    if (f - d >= 15 * 60_000) return { debut: d, fin: f, mode, enCours: f === plafond && plafond < finNuit }
  }
  return { debut: debutNuit, fin: plafond, mode: 'journee', enCours: plafond < finNuit }
}

/** Ramène un instant dans la fenêtre, à la minute. */
export function borner(ms: number, f: Fenetre): number {
  const m = Math.round(ms / 60_000) * 60_000
  return Math.min(f.fin, Math.max(f.debut, m))
}

/** Position en % d'un instant dans la fenêtre (bornée à 0..100). */
export function position(ms: number, f: Fenetre): number {
  const span = f.fin - f.debut
  if (span <= 0) return 0
  return Math.min(100, Math.max(0, ((ms - f.debut) / span) * 100))
}

/** Repères horaires : chaque heure pleine de Paris dans la fenêtre (une heure UTC pleine en est une : Paris décale d'heures entières). */
export function reperesHoraires(f: Fenetre): { at: number; libelle: string }[] {
  const out: { at: number; libelle: string }[] = []
  let t = Math.ceil(f.debut / 3_600_000) * 3_600_000
  for (; t <= f.fin; t += 3_600_000) {
    const p = partiesParis(t)
    out.push({ at: t, libelle: heureDouble(t) ? `${p.h} h ${decalageParis(t) >= 120 ? 'été' : 'hiver'}` : `${p.h} h` })
  }
  return out
}

/** Intervalle [début, fin] d'un trou de supervision, borné à la fenêtre ; null s'il n'y touche pas. */
export function trouDansFenetre(t: TrouSupervision, f: Fenetre): { de: number; a: number } | null {
  const de = Date.parse(t.from)
  const a = t.to ? Date.parse(t.to) : f.fin
  if (!Number.isFinite(de) || a < f.debut || de > f.fin) return null
  return { de: Math.max(de, f.debut), a: Math.min(a, f.fin) }
}

/** Trous actifs à l'instant T. */
export const trousA = (trous: TrouSupervision[], at: number): TrouSupervision[] =>
  trous.filter((t) => Date.parse(t.from) <= at && (t.to === null || Date.parse(t.to) > at))

export function totalTrous(trous: TrouSupervision[]): { nombre: number; secondes: number } {
  return { nombre: trous.length, secondes: trous.reduce((s, t) => s + (Number.isFinite(t.seconds) ? t.seconds : 0), 0) }
}

/* ═══════════════ Couleurs et mots ═══════════════ */

export const TON_RAPPORT: Record<string, Ton> = { ok: 'ok', attention: 'warn', probleme: 'err', inconnu: 'gris' }
export const LIBELLE_RAPPORT: Record<string, string> = { ok: 'Rien à signaler', attention: 'À voir', probleme: 'Problème', inconnu: 'Inconnu' }
export const TON_PREVOL: Record<string, Ton> = { green: 'ok', amber: 'warn', red: 'err', unknown: 'gris' }
export const LIBELLE_PREVOL: Record<string, string> = { green: 'Prêt', amber: 'À vérifier', red: 'Bloquant', unknown: 'Inconnu' }
export const TON_VERIF: Record<string, Ton> = { ok: 'ok', warn: 'warn', fail: 'err', unknown: 'gris', waiting: 'gris', skipped: 'off' }
export const LIBELLE_VERIF: Record<string, string> = { ok: 'OK', warn: 'À vérifier', fail: 'Échec', unknown: 'Inconnu', waiting: 'En attente', skipped: 'Non concerné' }
export const LIBELLE_TYPE: Record<string, string> = { soiree: 'Soirée', salle_allumee: 'Salle allumée', fermee: 'Fermé' }
export const LIBELLE_ORIGINE: Record<string, string> = {
  gestion: 'programme de Gestion', manual: 'saisie à la main', fallback: 'planning de secours', room_on: 'une salle s’est allumée',
}
export const GENRES_ANNOTATION: { value: GenreAnnotation; label: string }[] = [
  { value: 'note', label: 'Note' }, { value: 'balance', label: 'Balances' }, { value: 'intervention', label: 'Intervention' },
  { value: 'incident', label: 'Incident' }, { value: 'information', label: 'Information' },
]
export const LIBELLE_DETAIL: Record<string, string> = {
  raw: 'Détail complet conservé',
  partial: 'Détail brut purgé : moyennes seulement',
  none: 'Nuit à venir : rien d’enregistré',
}
export const LIBELLE_RESOLUTION: Record<string, string> = {
  raw: 'mesures brutes', '5m': 'moyennes sur 5 min', '1h': 'moyennes horaires', none: 'aucune mesure conservée',
}

/**
 * Teinte d'une nuit dans la liste. Le verdict du rapport du matin prime ;
 * sans rapport, les comptes de Sonde (un incident critique = rouge, un
 * incident = ambre). Sans comptes : gris (on ne sait pas). Une nuit fermée et
 * calme est « éteinte comme prévu », jamais verte par défaut.
 */
export function tonNuit(n: Pick<Nuit, 'report' | 'counts' | 'operating' | 'status'>): { ton: Ton; libelle: string; source: 'rapport' | 'comptes' | 'aucune' } {
  if (n.report) return { ton: TON_RAPPORT[n.report.status] ?? 'gris', libelle: LIBELLE_RAPPORT[n.report.status] ?? n.report.status, source: 'rapport' }
  const c = n.counts
  if (!c) return { ton: 'gris', libelle: 'Comptes indisponibles', source: 'aucune' }
  if (c.incidents_critical > 0) return { ton: 'err', libelle: `${c.incidents_critical} incident${c.incidents_critical > 1 ? 's' : ''} critique${c.incidents_critical > 1 ? 's' : ''}`, source: 'comptes' }
  if (c.incidents > 0) return { ton: 'warn', libelle: `${c.incidents} incident${c.incidents > 1 ? 's' : ''}`, source: 'comptes' }
  if (n.operating === false) return { ton: 'off', libelle: 'Club fermé, rien à signaler', source: 'comptes' }
  return { ton: n.status === 'open' ? 'gris' : 'ok', libelle: n.status === 'open' ? 'En cours, rien pour l’instant' : 'Sans incident', source: 'comptes' }
}

/* ═══════════════ Salles à l'instant T ═══════════════ */

const ORDRE_ETATS: EtatAffiche[] = ['OFFLINE', 'DEGRADED', 'MAINTENANCE', 'ONLINE', 'OFF_EXPECTED', 'UNKNOWN', 'STALE', 'UNAVAILABLE']
export const ETATS_AFFICHES = ORDRE_ETATS

export interface SalleAuRejeu {
  code: string
  power: 'on' | 'off' | 'unknown' | null
  since: string | null
  cut: boolean
  comptes: Partial<Record<EtatAffiche, number>>
  total: number
  incidents: number
  ton: Ton
}

/** Salles principales, toujours montrées en tête (même vides). */
export const SALLES_PRINCIPALES = ['R1', 'R2', 'R3']

/**
 * Regroupe le rejeu par salle : alimentation (dernier événement connu),
 * appareils comptés par état AFFICHÉ (STALE et UNAVAILABLE à part), incidents
 * ouverts à T dont la cause est dans la salle. Teinte : rose si coupure ou
 * appareil hors ligne, ambre si dégradé, émeraude si quelque chose est en
 * ligne, bleuté si la salle est éteinte, gris sinon (on ne sait pas).
 */
export function sallesAuRejeu(r: Pick<Rejeu, 'zones' | 'devices' | 'incidents' | 'zone'>): SalleAuRejeu[] {
  const par = new Map<string, SalleAuRejeu>()
  const salle = (code: string) => {
    let s = par.get(code)
    if (!s) { s = { code, power: null, since: null, cut: false, comptes: {}, total: 0, incidents: 0, ton: 'gris' }; par.set(code, s) }
    return s
  }
  if (!r.zone) for (const c of SALLES_PRINCIPALES) salle(c)
  for (const z of r.zones) { const s = salle(z.code); s.power = z.power; s.since = z.since; s.cut = z.cut }
  const zoneDe = new Map<string, string>()
  for (const d of r.devices) {
    const code = d.zone ?? '—'
    if (d.zone) zoneDe.set(d.uid, d.zone)
    const s = salle(code)
    s.comptes[d.display_state] = (s.comptes[d.display_state] ?? 0) + 1
    s.total++
  }
  for (const i of r.incidents) {
    if (!i.open_at_t || !i.root_uid) continue
    const z = zoneDe.get(i.root_uid)
    if (z) salle(z).incidents++
  }
  for (const s of par.values()) {
    const c = s.comptes
    s.ton = s.cut || (c.OFFLINE ?? 0) > 0 ? 'err'
      : (c.DEGRADED ?? 0) > 0 ? 'warn'
        : (c.ONLINE ?? 0) > 0 ? 'ok'
          : (c.MAINTENANCE ?? 0) > 0 ? 'maint'
            : s.power === 'off' || (c.OFF_EXPECTED ?? 0) > 0 ? 'off'
              : s.power === 'on' ? 'ok' : 'gris'
  }
  const rang = (code: string) => { const i = SALLES_PRINCIPALES.indexOf(code); return i < 0 ? (code === '—' ? 99 : 10) : i }
  return [...par.values()].sort((a, b) => rang(a.code) - rang(b.code) || a.code.localeCompare(b.code, 'fr'))
}

/** Appareils à montrer d'abord : ce qui n'est pas « en ligne » ni « éteint comme prévu », le pire en tête. */
export function appareilsATraiter(devices: AppareilRejeu[]): AppareilRejeu[] {
  const rang = (e: string) => { const i = ORDRE_ETATS.indexOf(e as EtatAffiche); return i < 0 ? 50 : i }
  return devices
    .filter((d) => d.display_state !== 'ONLINE' && d.display_state !== 'OFF_EXPECTED')
    .sort((a, b) => rang(a.display_state) - rang(b.display_state) || a.name.localeCompare(b.name, 'fr'))
}

/** Chemin relayé du rejeu (`at` à la minute, en UTC). */
export function cheminRejeu(date: string, at: number, zone: string | null): string {
  const q = new URLSearchParams({ at: new Date(at).toISOString() })
  if (zone) q.set('zone', zone)
  return `nights/${date}/replay?${q.toString()}`
}

/** Un chemin interne produit par Sonde n'est suivi que s'il reste dans /infrastructure. */
export const cheminSur = (p: string | null | undefined): string | null =>
  typeof p === 'string' && /^\/infrastructure(\/[A-Za-z0-9._~:/-]*)?$/.test(p) && !p.includes('//') ? p : null

/** Libellé humain d'une source (code de Sonde) à défaut de son libellé publié. */
export const libelleSource = (code: string, libelles: Map<string, string>): string => libelles.get(code) ?? code.replace(/_/g, ' ')
